/***
*
*   APP NAV
*   Primary navigation used inside the main app component
*
*   PROPS
*   type: fixed or popup (style of nav on mobile)
*   items: array of objects containing label, link and icon (optional)
*
**********/
import React, { useState, useEffect } from 'react';
import ClassNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { Button, Icon } from 'components/lib';
import './app.scss';

export function AppNav(props) {
  const [open, setOpen] = useState(false); // Mobile menu state
  const [expanded, setExpanded] = useState(false); // Desktop expanded state
  const [openSubmenu, setOpenSubmenu] = useState(null); // Track open submenu
  
  // Initialize sticky state from localStorage
  const [isSticky, setIsSticky] = useState(() => {
    try {
      const stored = localStorage.getItem('ledgerview-menu-sticky');
      return stored === 'true';
    } catch (error) {
      console.warn('Failed to read sticky menu preference from localStorage:', error);
      return false;
    }
  });

  const toggleSubmenu = (label) => {
    setOpenSubmenu(label); // Open the clicked submenu
  };

  const closeSubmenu = () => {
    setOpenSubmenu(null); // Close the submenu
  };

  // Notify parent of initial sticky state on mount
  useEffect(() => {
    if (props.onStickyChange) {
      props.onStickyChange(isSticky);
    }
    // Set expanded state if sticky is enabled
    if (isSticky) {
      setExpanded(true);
    }
  }, [isSticky, props]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const toggleStickyMode = () => {
    const newStickyState = !isSticky;
    setIsSticky(newStickyState);
    
    // Save to localStorage
    try {
      localStorage.setItem('ledgerview-menu-sticky', newStickyState.toString());
    } catch (error) {
      console.warn('Failed to save sticky menu preference to localStorage:', error);
    }
    
    if (newStickyState) {
      setExpanded(true); // Expand when enabling sticky mode
    }
    
    // Notify parent component of sticky state change
    if (props.onStickyChange) {
      props.onStickyChange(newStickyState);
    }
  };

  const renderSubmenu = (submenu, parentLabel) => {
    return (
      submenu.map((subItem, index) => (
        <NavLink
          key={`${parentLabel}-${subItem.label}-${index}`}
          to={subItem.link}
          className="submenu-item"
        >
          {subItem.icon && (
            <Icon
              className="icon"
              image={subItem.icon}
              size={open ? 15 : 18}
              color={open ? 'dark' : 'light'}
            />
          )}
          {subItem.label && <span className="label">{subItem.label}</span>}
        </NavLink>
      ))
    );
  };

  return (
    <nav
      className={ClassNames({
        sidebar: true,
        open: open,
        expanded: expanded,
        sticky: isSticky,
        [`mobile-${props.type}`]: props.type,
      })}
      onMouseEnter={() => !isSticky && setExpanded(true)}
      onMouseLeave={() => {
        if (!isSticky) {
          setExpanded(false);
          closeSubmenu(); // Close submenu when mouse leaves the menu
        }
      }}
    >
      {props.type === 'popup' && (
        <Button
          icon={open ? 'x' : 'menu'}
          color={'dark'}
          size={20}
          className="btn-togglenav"
          action={() => setOpen(!open)}
        />
      )}

      <div 
        className="logo-container"
        onClick={toggleStickyMode}
        title={isSticky ? 'Disable sticky mode' : 'Enable sticky mode'}
      >
        <img src={'/images/LV-White-Icon.png'} alt='Logo' />
      </div>
      <section className="nav-links">
        {props.items?.map((item) => (
          <React.Fragment key={item.label}>
            <div
              className={`menu-item ${item.submenu && 'has-submenu'}`}
              onMouseOver={() => {
                if (item.submenu) toggleSubmenu(item.label);
              }}
              onMouseLeave={() => {
                if (item.submenu) closeSubmenu();
              }}
              onClick={() => {
                if (item.link) window.location = item.link;
              }}
            >
              {item.icon && (
                <Icon
                  className="icon"
                  image={item.icon}
                  size={open ? 15 : 18}
                  color={open ? 'dark' : 'light'}
                />
              )}
              {item.label && <span className="label">{item.label}</span>}
              {item.submenu && expanded && (
                <Icon
                  className="chevron-icon"
                  image="chevron-right"
                  size={18}
                  color="light"
                />
              )}
              
              {/* Submenu as absolute positioned overlay */}
              {item.submenu && openSubmenu === item.label && (
                <div
                  className="submenu-dropdown"
                  onMouseEnter={() => toggleSubmenu(item.label)}
                  onMouseLeave={closeSubmenu}
                >
                  {renderSubmenu(item.submenu, item.label)}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </section>

      <section style={{ position: 'absolute', bottom: 1 }}>
        <section className="nav-links">
          {props.bottomItems?.map((item) => {
            if (item.link) {
              return (
                <NavLink
                  key={item.label}
                  to={item.link}
                  style={{ width: `${100 / props.items.length}%` }}
                >
                  {item.icon && (
                    <Icon
                      className="icon"
                      image={item.icon}
                      size={open ? 15 : 18}
                      color={open ? 'dark' : 'light'}
                    />
                  )}
                  {item.label && <span className="label">{item.label}</span>}
                </NavLink>
              );
            }

            return (
              <div key={item.label} onClick={item.action}>
                {item.icon && (
                  <Icon
                    className="icon"
                    image={item.icon}
                    size={open ? 15 : 18}
                    color={open ? 'dark' : 'light'}
                  />
                )}
                {item.label && <span className="label">{item.label}</span>}
              </div>
            );
          })}
        </section>
      </section>
    </nav>
  );
}
